import React from 'react'
import PropTypes from 'prop-types'
import qs from 'query-string'
import {
  Box,
  Button,
  Flex,
  Input,
  Radio,
  Label,
  FormField
} from 'pcln-design-system'
import { Arrival, Calendar as CalendarIcon, Departure } from 'pcln-icons'
import { Calendar } from '@pcln/date-picker'
import { TravelerSelection } from '../traveler-selection'
import TypeAheadContainer from '@pcln/typeahead'
import styled from 'styled-components'
import { PopoverContainer } from '../Popover'
import { getDateRange } from '../../utils/date-helpers'
import { CALENDAR_POPOVER_SELECTOR } from '../../constants/Constants'
import makeFeedbackAjaxCall from '../../utils/typeahead-feedback'

const ONE_WAY = 'one-way'
const ROUND_TRIP = 'round-trip'

const FLIGHT_ONLY = 'Flight Only'
const FLIGHT_HOTEL = 'Flight + Hotel'
const FLIGHT_HOTEL_CAR = 'Flight + Hotel + Car'

const ECONOMY = 'Economy'
const PREMIUM_ECONOMY = 'Premium Economy'
const BUSINESS_CLASS = 'Business'
const FIRST_CLASS = 'First'

const productTypes = [FLIGHT_ONLY, FLIGHT_HOTEL, FLIGHT_HOTEL_CAR]

const cabinClasses = [ECONOMY, PREMIUM_ECONOMY, BUSINESS_CLASS, FIRST_CLASS]

Label.nowrap = styled(Label)`
  white-space: nowrap;
`

const config = require('isomorphic-config')
const typeaheadFeedbackURL = config.client.typeahead.feedback.flights
const typeaheadFeedback = makeFeedbackAjaxCall(typeaheadFeedbackURL)

class SOPQFlightSearchForm extends React.Component {
  static propTypes = {
    // todo: update to match expected shape
    // this is loosely based on the flights form in d-home
    onChange: PropTypes.func,
    onFlightChange: PropTypes.func,
    onTravelerChange: PropTypes.func,
    onSubmit: PropTypes.func,
    update: PropTypes.func,
    openSOPQCalendar: PropTypes.func,
    tripType: PropTypes.string,
    handleDocumentClick: PropTypes.func,
    onTypeaheadSelection: PropTypes.func,
    closeSOPQCalendar: PropTypes.func,
    handleCalendarChange: PropTypes.func,
    departureDate: PropTypes.string,
    dropdownItems: PropTypes.object,
    calendarOpen: PropTypes.bool,
    arrivalDate: PropTypes.string,
    airDisclaimer: PropTypes.string,
    errors: PropTypes.object,
    withFooter: PropTypes.bool,
    adultsCount: PropTypes.number,
    youthsCount: PropTypes.number,
    infantsCount: PropTypes.number,
    childrenCount: PropTypes.number
  }

  static defaultProps = {
    tripType: 'round-trip',
    adultsCount: 1,
    youthsCount: 0,
    childrenCount: 0,
    infantsCount: 0,
    cabinClass: 'Economy',
    productType: 'Flight Only',
    // constants
    cabinClasses,
    productTypes,
    airDisclaimer: 'You may only book a flight for up to 8 ticketed passengers.',
    withFooter: true
  }

  componentDidMount () {
    document.addEventListener('click', this.props.handleDocumentClick)
    this.focusCloseButton()
  }

  componentWillUnmount () {
    document.removeEventListener('click', this.props.handleDocumentClick)
  }

  focusCloseButton (event) {
    const modalCloseButton = document.querySelector("#nyop-modal button[title='close']")
    const searchLocation = window && window.location ? window.location.search : ''
    const search = qs.parse(searchLocation)

    if (search.modal === 'express-deals' && modalCloseButton) {
      modalCloseButton.focus()
      if (event) { event.preventDefault() }
    }
  }

  render () {
    const {
      onChange,
      onFlightChange,
      onTravelerChange,
      onSubmit,
      openSOPQCalendar,
      onTypeaheadSelection,
      closeSOPQCalendar,
      handleCalendarChange,
      update,
      tripType,
      departureDate,
      arrivalDate,
      dropdownItems,
      calendarOpen,
      airDisclaimer,
      errors = {},
      withFooter,
      adultsCount,
      youthsCount,
      infantsCount,
      childrenCount
    } = this.props

    const departureDropdownItems = dropdownItems?.departureAirport
    const arrivalDropdownItems = dropdownItems?.arrivalAirport
    const isOneWay = tripType === ONE_WAY
    return (
      <form onSubmit={onSubmit}>
        <Flex mb={2}>
          <Box mr={3}>
            <Label.nowrap>
              <Radio
                id='trip-type-round-trip'
                name='tripType'
                checked={tripType === ROUND_TRIP}
                onChange={() => onChange({ tripType: ROUND_TRIP })}
              />{' '}
              Round-trip
            </Label.nowrap>
          </Box>
          <Box mr={3}>
            <Label.nowrap>
              <Radio
                mr={1}
                id='trip-type-one-way'
                name='tripType'
                checked={tripType === ONE_WAY}
                onChange={() => onChange({ tripType: ONE_WAY })}
              />{' '}
              One-way
            </Label.nowrap>
          </Box>
        </Flex>
        <Box>
          <Flex mx={-1} flexWrap='wrap'>
            <Box width={[1, 1, 1 / 3]} px={1} py={1}>
              <TypeAheadContainer
                id={`flight-departure-airport`}
                type='text'
                searchItems={departureDropdownItems}
                dropdownItems={departureDropdownItems}
                label='Where from?'
                placeholder='Where from?'
                onTypeAheadChange={e => {
                  onFlightChange({
                    departureAirport: e.target.value
                  })
                }}
                onItemSelection={item => {
                  onTypeaheadSelection('departureAirport', item)
                  typeaheadFeedback({ ...item, selectedPosition: departureDropdownItems?.indexOf(item) })
                }}
                isUsingErrorMessage={hasErrors(errors, 'departureAirport')}
                errorMessage={getErrorMessage(errors, 'departureAirport')}
                inputProps={{
                  icon: <Departure color='primary' />,
                  autoHide: true
                }}
                selectOnClick
              />
            </Box>
            <Box width={[1, 1, 1 / 3]} px={1} py={1}>
              <TypeAheadContainer
                id={`flight-arrival-airport`}
                type='text'
                searchItems={arrivalDropdownItems}
                dropdownItems={arrivalDropdownItems}
                label='Where to?'
                placeholder='Where to?'
                onTypeAheadChange={e => {
                  onFlightChange({
                    arrivalAirport: e.target.value
                  })
                }}
                onItemSelection={item => {
                  onTypeaheadSelection('arrivalAirport', item)
                  typeaheadFeedback({ ...item, selectedPosition: arrivalDropdownItems?.indexOf(item) })
                }}
                isUsingErrorMessage={hasErrors(errors, 'arrivalAirport')}
                errorMessage={getErrorMessage(errors, 'arrivalAirport')}
                inputProps={{
                  icon: <Arrival color='primary' />,
                  autoHide: true
                }}
                selectOnClick
              />
            </Box>
            <Flex
              alignItems='center'
              justifyContent='stretch'
              width={[1, 1, 1 / 3]}
              px={1}
              py={1}
            >
              <PopoverContainer
                key='dates'
                data-calendar
                open={calendarOpen}
                position='right'
                onDismiss={() => {
                  update(closeSOPQCalendar)
                }}
                domSelectorForVisuallyContainedElement={CALENDAR_POPOVER_SELECTOR}
                role='application'
              >
                <FormField>
                  <Label autoHide>
                    {isOneWay ? 'Departing' : 'Departing – Returning'}
                  </Label>
                  <CalendarIcon color='primary' />
                  <Input
                    m={0}
                    width={1 / 2}
                    id={`flight-date-range`}
                    placeholder={
                      isOneWay ? 'Departing' : 'Departing – Returning'
                    }
                    autoComplete='off'
                    value={getDateRange(
                      { departureDate, arrivalDate },
                      isOneWay
                    )}
                    readOnly
                    onClick={e => {
                      update(openSOPQCalendar())
                    }}
                    onFocus={e => {
                      update(openSOPQCalendar())
                    }}
                    color={getBorderColors(errors)}
                    aria-expanded={calendarOpen ? 'true' : 'false'}
                  />
                </FormField>
                <Calendar
                  id={`calendar-0`}
                  onChange={(e, dates) => {
                    handleCalendarChange({
                      isOneWay,
                      dates
                    })
                  }}
                  startDate={departureDate}
                  endDate={!isOneWay ? arrivalDate : undefined}
                  monthWidthPx={300}
                  selectTwoDates={!isOneWay}
                  allowSameDay
                  useDefaultFooter={withFooter}
                  onDismiss={() => update(closeSOPQCalendar)}
                />
              </PopoverContainer>
            </Flex>
          </Flex>

          <Flex mx={-1} flexWrap={['wrap', 'wrap', 'nowrap']} justifyContent={'stretch'}>
            <Box width={[1, 1, 1 / 2]} px={1} py={1}>
              <TravelerSelection
                isUsingAdultControl
                isUsingYouthControl
                isUsingChildControl
                isUsingLapInfantControl
                isUsingErrorTooltip
                initNumAdults={adultsCount || 1}
                initNumYouths={youthsCount || 0}
                initNumInfants={infantsCount || 0}
                initNumChildren={childrenCount || 0}
                initDisplayLabel='1 Adult'
                minAdultCount={1}
                disclaimer={airDisclaimer}
                error={
                  errors.infantsCount
                    ? 'Priceline does not support infants on international flights.' // TODO: Refactor error message to come from validator
                    : ''
                }
                onChange={changedData => {
                  if (typeof onTravelerChange === 'function') {
                    onTravelerChange(changedData)
                  }
                }}
              />
            </Box>
          </Flex>
        </Box>
        <Flex pt={1} pb={3}>
          <Box mx='auto' />
          <Button
            color='secondary'
            onKeyDown={(e) => {
              this.focusCloseButton(e)
            }}
            width={[1, 1 / 3]}
          >
            Find your flight
          </Button>
        </Flex>
      </form>
    )
  }
}

function getErrorMessage (errors, key) {
  return hasErrors(errors, key) ? 'Enter an airport' : false
}

function hasErrors (errors, key) {
  return errors && errors[key] ? !!errors[key] : false
}

function getBorderColors (errors) {
  return (errors && errors.departureDate) || (errors && errors.arrivalDate)
    ? 'red'
    : undefined
}

export default SOPQFlightSearchForm
